import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import {
	Briefcase,
	Calendar,
	ChevronLeft,
	ChevronRight,
	Circle,
	Coffee,
	Clock,
	Edit2,
	MapPin,
	Play,
} from 'react-feather';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import SimpleClock from '../components/SimpleClock';
import AttendanceLocationMap, {
	hasAttendanceLocation,
} from '../components/AttendanceLocationMap';
import ClockCaptureModal from '../components/ClockCaptureModal';
import { uploadFile } from '../utils/fileUpload';
import { normalizePhotoUrl } from '../utils/photoUrl';
import {
	createAttendance,
	fetchAttendance,
	fetchAttendanceForUser,
	updateAttendance,
} from '../slices/attendanceSlice';
import { fetchSelf, fetchUsers } from '../slices/userSlice';
import {
	EDITABLE_ATTENDANCE_USER_IDS,
	buildWeekSummaries,
	formatAttendanceDate,
	formatAttendanceTime,
	formatDateTimeLocalValue,
	formatDuration,
	getDailyAttendanceSummary,
	getDurationMinutes,
	getNextWeek,
	getPreviousWeek,
	getWeekLabel,
	getWeeklyTotals,
	getWeekRange,
	hasInvalidDuration,
	isRecordInWeek,
	parseAttendanceDate,
	sortAttendanceRecords,
} from '../utils/attendance';

function SummaryCard({ title, value, hint, icon: Icon, tone = 'text-secondColor-600' }) {
	return (
		<div className="rounded-md border border-gray-200 bg-white p-4 shadow-sm">
			<div className="flex items-start justify-between gap-3">
				<div>
					<p className="text-sm text-gray-500">{title}</p>
					<p className="mt-2 text-2xl font-semibold text-gray-900">{value}</p>
					{hint ? <p className="mt-2 text-sm text-gray-500">{hint}</p> : null}
				</div>
				<div className={`rounded-md bg-gray-100 p-2 ${tone}`}>
					<Icon size={18} />
				</div>
			</div>
		</div>
	);
}

function SectionCard({ title, subtitle, children, action }) {
	return (
		<section className="rounded-md border border-gray-200 bg-white p-5 shadow-sm">
			<div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
				<div>
					<h2 className="text-xl font-semibold text-gray-900">{title}</h2>
					{subtitle ? <p className="mt-1 text-sm text-gray-500">{subtitle}</p> : null}
				</div>
				{action}
			</div>
			{children}
		</section>
	);
}

function EmptyRow({ colSpan, label }) {
	return (
		<tr>
			<td colSpan={colSpan} className="px-4 py-8 text-center text-sm text-gray-500">
				{label}
			</td>
		</tr>
	);
}

function StatusPill({ label, tone = 'slate' }) {
	const styles = {
		green: 'bg-green-100 text-green-700',
		amber: 'bg-amber-100 text-amber-700',
		red: 'bg-red-100 text-red-700',
		slate: 'bg-slate-100 text-slate-700',
	};

	return (
		<span
			className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${styles[tone]}`}
		>
			{label}
		</span>
	);
}

function getUserDisplayName(user) {
	if (!user) {
		return 'Unknown employee';
	}

	const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
	return fullName || user.userName || user.email || 'Unknown employee';
}

function isUserActive(user) {
	return Boolean(user?.isActive ?? user?.active);
}

function getOngoingDurationMinutes(record, currentTime) {
	const checkIn = parseAttendanceDate(record?.checkInTime);

	if (!checkIn || !currentTime) {
		return null;
	}

	const durationMinutes = Math.floor(
		(currentTime.getTime() - checkIn.getTime()) / (1000 * 60)
	);

	return durationMinutes >= 0 ? durationMinutes : null;
}

function getRecordStatus(record) {
	if (!record?.checkOutTime) {
		return { label: 'Open', tone: 'amber' };
	}

	if (hasInvalidDuration(record)) {
		return { label: 'Needs review', tone: 'red' };
	}

	return { label: 'Closed', tone: 'green' };
}

function getClockInDeviceGate() {
	if (typeof navigator === 'undefined') {
		return { isPhoneBlocked: false, isIPod: false };
	}

	const userAgent = String(navigator.userAgent || '');
	const isIPod = /iPod/i.test(userAgent);

	if (isIPod) {
		return { isPhoneBlocked: false, isIPod: true };
	}

	const isIPhone = /iPhone/i.test(userAgent);
	const isAndroidPhone = /Android/i.test(userAgent) && /Mobile/i.test(userAgent);
	const isWindowsPhone = /Windows Phone/i.test(userAgent);
	const isOtherMobilePhone =
		/Mobi|Mobile/i.test(userAgent) &&
		!/iPad/i.test(userAgent) &&
		!/Tablet/i.test(userAgent);

	return {
		isPhoneBlocked: Boolean(
			isIPhone || isAndroidPhone || isWindowsPhone || isOtherMobilePhone
		),
		isIPod: false,
	};
}

function PhoneClockInBlocked() {
	return (
		<div className="mx-auto w-full max-w-md rounded-md border border-red-200 bg-red-50 p-5 text-center shadow-sm">
			<p className="text-lg font-semibold text-red-800">Clock-in is disabled on phones</p>
			<p className="mt-2 text-sm text-red-700">
				Please use an iPod touch or a computer to clock in / out.
			</p>
		</div>
	);
}

function DailySummaryTable({ summaries }) {
	return (
		<div className="overflow-x-auto">
			<table className="min-w-full border-collapse text-left">
				<thead>
					<tr className="border-b border-gray-200 text-sm text-gray-500">
						<th className="px-4 py-3 font-medium">Day</th>
						<th className="px-4 py-3 font-medium">First In</th>
						<th className="px-4 py-3 font-medium">Last Out</th>
						<th className="px-4 py-3 font-medium">Paid Hours</th>
						<th className="px-4 py-3 font-medium">Break / Exit</th>
						<th className="px-4 py-3 font-medium">Break Count</th>
						<th className="px-4 py-3 font-medium">Status</th>
					</tr>
				</thead>
				<tbody>
					{summaries.map((summary) => {
						const status = summary.openBreakRecord
							? { label: 'On paid break', tone: 'amber' }
							: summary.openWorkRecord
								? { label: 'Clocked in', tone: 'green' }
								: summary.hasInvalidRecords
									? { label: 'Needs review', tone: 'red' }
									: summary.records.length
										? { label: 'Closed', tone: 'slate' }
										: { label: '--', tone: 'slate' };

						return (
							<tr
								key={summary.date.toISOString()}
								className="border-b border-gray-100 text-sm text-gray-700"
							>
								<td className="px-4 py-3">
									<div className="font-medium text-gray-900">
										{summary.date.toLocaleDateString('en-US', {
											weekday: 'short',
										})}
									</div>
									<div className="text-xs text-gray-500">
										{formatAttendanceDate(summary.date)}
									</div>
								</td>
								<td className="px-4 py-3">
									{summary.firstClockIn
										? formatAttendanceTime(summary.firstClockIn)
										: '--'}
								</td>
								<td className="px-4 py-3">
									{summary.lastClockOut
										? formatAttendanceTime(summary.lastClockOut)
										: '--'}
								</td>
								<td className="px-4 py-3">{formatDuration(summary.workMinutes)}</td>
								<td className="px-4 py-3">{formatDuration(summary.breakMinutes)}</td>
								<td className="px-4 py-3">{summary.breakCount}</td>
								<td className="px-4 py-3">
									<StatusPill label={status.label} tone={status.tone} />
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}

function TeamTotalsTable({ rows, selectedEmployeeId, onSelectEmployee }) {
	return (
		<div className="overflow-x-auto">
			<table className="min-w-full border-collapse text-left">
				<thead>
					<tr className="border-b border-gray-200 text-sm text-gray-500">
						<th className="px-4 py-3 font-medium">Employee</th>
						<th className="px-4 py-3 font-medium">Role</th>
						<th className="px-4 py-3 font-medium">Paid Hours</th>
						<th className="px-4 py-3 font-medium">Regular</th>
						<th className="px-4 py-3 font-medium">Overtime</th>
						<th className="px-4 py-3 font-medium">Break / Exit</th>
						<th className="px-4 py-3 font-medium">Break Count</th>
						<th className="px-4 py-3 font-medium">View</th>
					</tr>
				</thead>
				<tbody>
					{rows.length ? (
						rows.map((row) => {
							const isSelected = row.user.id === selectedEmployeeId;

							return (
								<tr
									key={row.user.id}
									className={`border-b border-gray-100 text-sm text-gray-700 ${
										isSelected ? 'bg-secondColor-50' : ''
									}`}
								>
									<td className="px-4 py-3">
										<div className="font-medium text-gray-900">
											{getUserDisplayName(row.user)}
										</div>
										<div className="text-xs text-gray-500">{row.user.email}</div>
									</td>
									<td className="px-4 py-3">{row.user.role || 'User'}</td>
									<td className="px-4 py-3">{formatDuration(row.totals.paidMinutes)}</td>
									<td className="px-4 py-3">
										{formatDuration(row.totals.regularMinutes)}
									</td>
									<td className="px-4 py-3">
										{formatDuration(row.totals.overtimeMinutes)}
									</td>
									<td className="px-4 py-3">
										{formatDuration(row.totals.breakMinutes)}
									</td>
									<td className="px-4 py-3">{row.totals.breakCount}</td>
									<td className="px-4 py-3">
										<button
											type="button"
											onClick={() => onSelectEmployee(row.user.id)}
											className="rounded-md border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
										>
											{isSelected ? 'Viewing' : 'View'}
										</button>
									</td>
								</tr>
							);
						})
					) : (
						<EmptyRow colSpan={8} label="No employees found." />
					)}
				</tbody>
			</table>
		</div>
	);
}

function SessionTable({
	records,
	currentTime,
	emptyLabel,
	showUser = false,
	canEdit = false,
	onEdit,
	onViewLocation,
}) {
	const colSpan = 7 + (showUser ? 1 : 0) + (canEdit ? 1 : 0);

	return (
		<div className="overflow-x-auto">
			<table className="min-w-full border-collapse text-left">
				<thead>
					<tr className="border-b border-gray-200 text-sm text-gray-500">
						<th className="px-4 py-3 font-medium">Date</th>
						{showUser ? <th className="px-4 py-3 font-medium">Employee</th> : null}
						<th className="px-4 py-3 font-medium">Type</th>
						<th className="px-4 py-3 font-medium">Start</th>
						<th className="px-4 py-3 font-medium">End</th>
						<th className="px-4 py-3 font-medium">Duration</th>
						<th className="px-4 py-3 font-medium">Status</th>
						<th className="px-4 py-3 font-medium">Location</th>
						{canEdit ? <th className="px-4 py-3 font-medium">Edit</th> : null}
					</tr>
				</thead>
				<tbody>
					{records.length ? (
						records.map((record) => {
							const status = getRecordStatus(record);
							const savedDuration = getDurationMinutes(record);
							const liveDuration =
								record?.checkOutTime || hasInvalidDuration(record)
									? null
									: getOngoingDurationMinutes(record, currentTime);
							const durationLabel = record?.checkOutTime
								? savedDuration === null
									? 'Needs review'
									: formatDuration(savedDuration)
								: formatDuration(liveDuration);

							return (
								<tr
									key={record.id}
									className="border-b border-gray-100 text-sm text-gray-700"
								>
									<td className="px-4 py-3">
										{formatAttendanceDate(record.date || record.checkInTime)}
									</td>
									{showUser ? (
										<td className="px-4 py-3">{record.userName || '--'}</td>
									) : null}
									<td className="px-4 py-3">
										<span
											className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
												record?.isBreak
													? 'bg-amber-100 text-amber-700'
													: 'bg-green-100 text-green-700'
											}`}
										>
											{record?.isBreak ? 'Paid break / exit' : 'Worked time'}
										</span>
									</td>
									<td className="px-4 py-3">
										{formatAttendanceTime(record.checkInTime)}
									</td>
									<td className="px-4 py-3">
										{record?.checkOutTime
											? formatAttendanceTime(record.checkOutTime)
											: '--'}
									</td>
									<td className="px-4 py-3">{durationLabel}</td>
									<td className="px-4 py-3">
										<StatusPill label={status.label} tone={status.tone} />
									</td>
									<td className="px-4 py-3">
										{hasAttendanceLocation(record) ||
										record?.fileIn ||
										record?.fileOut ? (
											<button
												type="button"
												onClick={() => onViewLocation(record)}
												className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-3 py-2 text-sm font-medium text-secondColor-600 transition hover:bg-gray-50"
											>
												<MapPin size={15} />
												View
											</button>
										) : (
											<span className="text-sm text-gray-400">--</span>
										)}
									</td>
									{canEdit ? (
										<td className="px-4 py-3">
											<button
												type="button"
												onClick={() => onEdit(record)}
												className="inline-flex items-center rounded-md border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
											>
												<Edit2 size={15} />
											</button>
										</td>
									) : null}
								</tr>
							);
						})
					) : (
						<EmptyRow colSpan={colSpan} label={emptyLabel} />
					)}
				</tbody>
			</table>
		</div>
	);
}

function EditAttendanceModal({
	record,
	values,
	onChange,
	onClose,
	onSubmit,
	isSaving,
}) {
	if (!record) {
		return null;
	}

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
			<div className="w-full max-w-xl rounded-md bg-white p-6 shadow-xl">
				<div className="mb-5 flex items-start justify-between gap-4">
					<div>
						<h3 className="text-xl font-semibold text-gray-900">Edit time entry</h3>
						<p className="mt-1 text-sm text-gray-500">
							{record?.isBreak ? 'Paid break / exit' : 'Worked time'}
						</p>
					</div>
					<button
						type="button"
						onClick={onClose}
						className="rounded-md border border-gray-200 px-3 py-2 text-sm font-medium text-gray-600 transition hover:bg-gray-50"
					>
						Close
					</button>
				</div>

				<form onSubmit={onSubmit} className="space-y-4">
					<div>
						<label className="mb-2 block text-sm font-medium text-gray-700">
							Start
						</label>
						<input
							type="datetime-local"
							name="checkInTime"
							value={values.checkInTime}
							onChange={onChange}
							className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
							required
						/>
					</div>

					<div>
						<label className="mb-2 block text-sm font-medium text-gray-700">
							End
						</label>
						<input
							type="datetime-local"
							name="checkOutTime"
							value={values.checkOutTime}
							onChange={onChange}
							className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
						/>
					</div>

					<div className="flex flex-wrap justify-end gap-3 pt-2">
						<button
							type="button"
							onClick={onClose}
							className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
						>
							Cancel
						</button>
						<button
							type="submit"
							disabled={isSaving}
							className="rounded-md bg-secondColor-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-secondColor-700 disabled:cursor-not-allowed disabled:opacity-70"
						>
							{isSaving ? 'Saving...' : 'Save changes'}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
}

function LocationDetail({ label, latitude, longitude, photoUrl, tone }) {
	const lat = Number(latitude);
	const lng = Number(longitude);
	const hasCoordinates =
		Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0);
	const normalizedPhotoUrl = normalizePhotoUrl(photoUrl);
	const hasPhoto = normalizedPhotoUrl.length > 0;

	return (
		<div className="rounded-md border border-gray-200 bg-gray-50 p-4">
			<div className="flex items-center gap-2">
				<span className={`h-3 w-3 rounded-full ${tone}`} />
				<p className="text-sm font-medium text-gray-700">{label}</p>
			</div>

			{hasPhoto ? (
				<a href={normalizedPhotoUrl} target="_blank" rel="noopener noreferrer">
					<img
						src={normalizedPhotoUrl}
						alt={`${label} photo`}
						className="mt-3 h-32 w-full rounded-md border border-gray-200 object-cover"
						onError={(event) => {
							// Hide a broken thumbnail (e.g. object missing or not
							// publicly readable) and surface a note instead. The
							// <img> is wrapped in an <a>, so hide the anchor and
							// reveal its sibling note.
							const anchor = event.currentTarget.closest('a');
							if (anchor) {
								anchor.style.display = 'none';
								const note = anchor.nextElementSibling;
								if (note) {
									note.style.display = 'block';
								}
							}
						}}
					/>
				</a>
			) : null}

			{hasPhoto ? (
				<p
					className="mt-3 text-xs text-gray-400"
					style={{ display: 'none' }}
				>
					Photo unavailable
				</p>
			) : null}

			{hasCoordinates ? (
				<div className="mt-2 space-y-1">
					<p className="text-sm text-gray-900">
						{lat.toFixed(6)}, {lng.toFixed(6)}
					</p>
					<a
						href={`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`}
						target="_blank"
						rel="noopener noreferrer"
						className="text-sm font-medium text-secondColor-600 transition hover:text-secondColor-700"
					>
						Open in Google Maps
					</a>
				</div>
			) : (
				<p className="mt-2 text-sm text-gray-400">No location recorded</p>
			)}
		</div>
	);
}

function LocationModal({ record, onClose }) {
	if (!record) {
		return null;
	}

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
			<div className="w-full max-w-xl rounded-md bg-white p-6 shadow-xl">
				<div className="mb-5 flex items-start justify-between gap-4">
					<div>
						<h3 className="text-xl font-semibold text-gray-900">Location</h3>
						<p className="mt-1 text-sm text-gray-500">
							{formatAttendanceDate(record.date || record.checkInTime)} ·{' '}
							{record?.isBreak ? 'Paid break / exit' : 'Worked time'}
						</p>
					</div>
					<button
						type="button"
						onClick={onClose}
						className="rounded-md border border-gray-200 px-3 py-2 text-sm font-medium text-gray-600 transition hover:bg-gray-50"
					>
						Close
					</button>
				</div>

				<AttendanceLocationMap
					latitudeIn={record.latitudeIn}
					longitudeIn={record.longitudeIn}
					latitudeOut={record.latitudeOut}
					longitudeOut={record.longitudeOut}
					className="h-72 w-full"
				/>

				<div className="mt-4 grid gap-3 sm:grid-cols-2">
					<LocationDetail
						label="Clock in"
						latitude={record.latitudeIn}
						longitude={record.longitudeIn}
						photoUrl={record.fileIn}
						tone="bg-green-600"
					/>
					<LocationDetail
						label="Clock out"
						latitude={record.latitudeOut}
						longitude={record.longitudeOut}
						photoUrl={record.fileOut}
						tone="bg-red-600"
					/>
				</div>
			</div>
		</div>
	);
}

function getErrorMessage(error) {
	if (typeof error === 'string') {
		return error;
	}

	if (error?.message) {
		return error.message;
	}

	if (error?.Message) {
		return error.Message;
	}

	return 'Something went wrong. Please try again.';
}

function SickPayModal({ isOpen, onClose, onSubmit, isSaving, employeeLabel }) {
	const [dateValue, setDateValue] = useState(() => format(new Date(), 'yyyy-MM-dd'));
	const [hoursValue, setHoursValue] = useState('8');

	useEffect(() => {
		if (isOpen) {
			setDateValue(format(new Date(), 'yyyy-MM-dd'));
			setHoursValue('8');
		}
	}, [isOpen]);

	if (!isOpen) {
		return null;
	}

	const handleSubmit = (event) => {
		event.preventDefault();
		onSubmit({
			date: dateValue,
			hours: Number(hoursValue),
		});
	};

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6">
			<div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
				<div className="flex items-start justify-between gap-4">
					<div>
						<h2 className="text-lg font-semibold text-gray-900">Add Sick Pay Day</h2>
						<p className="mt-1 text-sm text-gray-600">
							Creates a paid time entry for {employeeLabel}.
						</p>
					</div>
					<button
						type="button"
						onClick={onClose}
						className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 transition hover:bg-gray-50"
						disabled={isSaving}
					>
						Close
					</button>
				</div>

				<form onSubmit={handleSubmit} className="mt-5 space-y-4">
					<div>
						<label className="text-sm font-medium text-gray-700">Date</label>
						<input
							type="date"
							value={dateValue}
							onChange={(event) => setDateValue(event.target.value)}
							className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900"
							required
						/>
					</div>

					<div>
						<label className="text-sm font-medium text-gray-700">Paid hours</label>
						<input
							type="number"
							min="0.25"
							step="0.25"
							value={hoursValue}
							onChange={(event) => setHoursValue(event.target.value)}
							className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900"
							required
						/>
						<p className="mt-2 text-xs text-gray-500">Defaults to an 8 hour paid entry.</p>
					</div>

					<div className="flex justify-end gap-3 pt-2">
						<button
							type="button"
							onClick={onClose}
							className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
							disabled={isSaving}
						>
							Cancel
						</button>
						<button
							type="submit"
							disabled={isSaving}
							className="rounded-md bg-secondColor-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-secondColor-700 disabled:cursor-not-allowed disabled:opacity-70"
						>
							{isSaving ? 'Adding...' : 'Add Sick Pay'}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
}

export default function Clockin() {
	const dispatch = useDispatch();
	const token = useSelector((state) => state.auth.token);
	const attendanceRecords = useSelector((state) => state.attendance.items || []);
	const attendanceStatus = useSelector((state) => state.attendance.listStatus);
	const attendanceSaveStatus = useSelector((state) => state.attendance.saveStatus);
	const selfAccount = useSelector((state) => state.auth.self);
	const users = useSelector((state) => state.auth.users || []);
	const deviceGate = useMemo(() => getClockInDeviceGate(), []);

	const [currentTime, setCurrentTime] = useState(new Date());
	const [selectedWeekStart, setSelectedWeekStart] = useState(
		() => getWeekRange().weekStart
	);
	const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
	const [editingRecord, setEditingRecord] = useState(null);
	const [editingValues, setEditingValues] = useState({
		checkInTime: '',
		checkOutTime: '',
	});
	const [isSavingEdit, setIsSavingEdit] = useState(false);
	const [locationRecord, setLocationRecord] = useState(null);
	const [pendingClockAction, setPendingClockAction] = useState(null);
	const [isClockSaving, setIsClockSaving] = useState(false);
	const [isSickPayModalOpen, setIsSickPayModalOpen] = useState(false);
	const [isSavingSickPay, setIsSavingSickPay] = useState(false);

	const selfAccountId = selfAccount?.id ? String(selfAccount.id).toLowerCase() : '';
	const canEditAttendance = EDITABLE_ATTENDANCE_USER_IDS.includes(selfAccountId);
	const isSuperUser = selfAccount?.role === 'SUPER';
	const canManageSickPay =
		selfAccount?.role === 'SUPER' || selfAccount?.role === 'ADMIN';
	const currentWeekStart = getWeekRange().weekStart;
	const isCurrentWeekView =
		selectedWeekStart.toDateString() === currentWeekStart.toDateString();
	const isBusy = attendanceStatus === 'loading' || attendanceSaveStatus === 'loading';

	useEffect(() => {
		if (!token || !selfAccount?.id) {
			return;
		}

		if (selfAccount?.role === 'SUPER') {
			dispatch(fetchAttendance());
			return;
		}

		dispatch(fetchAttendanceForUser({ userId: selfAccount.id }));
	}, [dispatch, token, selfAccount?.id, selfAccount?.role]);

	useEffect(() => {
		if (token && isSuperUser) {
			dispatch(fetchUsers({ pageSize: 1000, sortOrder: 'ASC' }));
		}
	}, [dispatch, token, isSuperUser]);

	useEffect(() => {
		if (!selfAccountId) {
			return;
		}

		setSelectedEmployeeId((currentSelectedEmployeeId) =>
			currentSelectedEmployeeId || selfAccountId
		);
	}, [selfAccountId]);

	const selfRecords = useMemo(() => {
		if (!selfAccountId) {
			return [];
		}

		return sortAttendanceRecords(
			attendanceRecords.filter(
				(record) =>
					String(record?.userId || '').toLowerCase() === selfAccountId
			)
		);
	}, [attendanceRecords, selfAccountId]);

	const activeEmployees = useMemo(
		() =>
			[...users]
				.filter((user) => isUserActive(user) && !user?.email?.includes('_deactivate_'))
				.sort((left, right) =>
					getUserDisplayName(left).localeCompare(getUserDisplayName(right))
				),
		[users]
	);

	const viewedEmployeeId = isSuperUser
		? selectedEmployeeId || selfAccountId
		: selfAccountId;

	const viewedEmployee = useMemo(() => {
		if (!isSuperUser) {
			return selfAccount;
		}

		return (
			activeEmployees.find(
				(user) => String(user?.id || '').toLowerCase() === viewedEmployeeId
			) || selfAccount
		);
	}, [activeEmployees, isSuperUser, selfAccount, viewedEmployeeId]);

	const viewedEmployeeLabel = getUserDisplayName(viewedEmployee);

	const viewedEmployeeRecords = useMemo(() => {
		if (!viewedEmployeeId) {
			return [];
		}

		return sortAttendanceRecords(
			attendanceRecords.filter(
				(record) =>
					String(record?.userId || '').toLowerCase() === viewedEmployeeId
			)
		);
	}, [attendanceRecords, viewedEmployeeId]);

	const viewedOpenWorkRecord = useMemo(
		() =>
			viewedEmployeeRecords.find(
				(record) => !record?.isBreak && !record?.checkOutTime
			) || null,
		[viewedEmployeeRecords]
	);

	const viewedOpenBreakRecord = useMemo(
		() =>
			viewedEmployeeRecords.find(
				(record) => record?.isBreak && !record?.checkOutTime
			) || null,
		[viewedEmployeeRecords]
	);

	const openWorkRecord = useMemo(
		() => selfRecords.find((record) => !record?.isBreak && !record?.checkOutTime) || null,
		[selfRecords]
	);

	const openBreakRecord = useMemo(
		() => selfRecords.find((record) => record?.isBreak && !record?.checkOutTime) || null,
		[selfRecords]
	);

	const todaySummary = useMemo(
		() => getDailyAttendanceSummary(viewedEmployeeRecords, new Date()),
		[viewedEmployeeRecords]
	);

	const personalWeekRecords = useMemo(
		() =>
			sortAttendanceRecords(
				viewedEmployeeRecords.filter((record) =>
					isRecordInWeek(record, selectedWeekStart)
				)
			),
		[viewedEmployeeRecords, selectedWeekStart]
	);

	const weekSummaries = useMemo(
		() => buildWeekSummaries(viewedEmployeeRecords, selectedWeekStart),
		[viewedEmployeeRecords, selectedWeekStart]
	);

	const weekTotals = useMemo(
		() => getWeeklyTotals(personalWeekRecords),
		[personalWeekRecords]
	);

	const teamTotalsRows = useMemo(() => {
		return activeEmployees.map((user) => {
			const userId = String(user?.id || '').toLowerCase();
			const employeeWeekRecords = attendanceRecords.filter(
				(record) =>
					String(record?.userId || '').toLowerCase() === userId &&
					isRecordInWeek(record, selectedWeekStart)
			);

			return {
				user,
				totals: getWeeklyTotals(employeeWeekRecords),
			};
		});
	}, [activeEmployees, attendanceRecords, selectedWeekStart]);

	const statusConfig = openBreakRecord
		? {
				label: 'On paid break',
				tone: 'amber',
				hint: 'End the break before clocking out.',
		  }
		: openWorkRecord
			? {
					label: 'Clocked in',
					tone: 'green',
					hint: 'You can start a paid break / exit or clock out.',
			  }
			: {
					label: 'Clocked out',
					tone: 'slate',
					hint: 'Start a new shift when you are ready.',
			  };

	const refreshAttendance = async () => {
		if (!selfAccount?.id) {
			return;
		}

		if (isSuperUser) {
			await dispatch(fetchAttendance());
			return;
		}

		await dispatch(fetchAttendanceForUser({ userId: selfAccount.id }));
	};

	// Clock actions require a camera photo + geolocation, so they open the
	// capture modal first instead of dispatching immediately.
	const requestClockAction = (action) => {
		if (deviceGate.isPhoneBlocked) {
			toast.error('Clock-in is disabled on phones. Please use an iPod or computer.');
			return;
		}

		if (!selfAccount?.id) {
			toast.error('Your account is still loading. Try again in a moment.');
			return;
		}

		if (action.kind === 'close' && !action.record?.id) {
			return;
		}

		setPendingClockAction(action);
	};

	const handleClockCapture = async ({ photoFile, latitude, longitude }) => {
		const action = pendingClockAction;

		if (!action || !selfAccount?.id) {
			return;
		}

		const now = new Date().toISOString();
		setIsClockSaving(true);

		try {
			// Upload the captured photo first; the API stores fileIn/fileOut as
			// the returned file URL, not the raw image data.
			let fileUrl = '';
			try {
				fileUrl = await uploadFile(photoFile);
			} catch (uploadError) {
				toast.error('We could not upload your photo. Please try again.');
				setIsClockSaving(false);
				return;
			}

			if (action.kind === 'create') {
				await dispatch(
					createAttendance({
						UserId: selfAccount.id,
						Date: now,
						CheckInTime: now,
						CheckOutTime: null,
						IsBreak: action.isBreak,
						LatitudeIn: latitude,
						LongitudeIn: longitude,
						...(fileUrl ? { FileIn: fileUrl } : null),
					})
				).unwrap();
			} else {
				const record = action.record;
				await dispatch(
					updateAttendance({
						id: record.id,
						requestedUserId: record.userId || selfAccount?.id,
						date: record.date || record.checkInTime,
						checkInTime: record.checkInTime,
						checkOutTime: now,
						isBreak: record.isBreak,
						// Preserve the clock-in location / photo on the update.
						latitudeIn: record.latitudeIn,
						longitudeIn: record.longitudeIn,
						fileIn: record.fileIn,
						latitudeOut: latitude,
						longitudeOut: longitude,
						...(fileUrl ? { fileOut: fileUrl } : null),
					})
				).unwrap();
			}

			await refreshAttendance();
			toast.success(action.successMessage);
			setPendingClockAction(null);
		} catch (error) {
			toast.error(getErrorMessage(error));
		} finally {
			setIsClockSaving(false);
		}
	};

	const handleEditOpen = (record) => {
		setEditingRecord(record);
		setEditingValues({
			checkInTime: formatDateTimeLocalValue(record?.checkInTime),
			checkOutTime: formatDateTimeLocalValue(record?.checkOutTime),
		});
	};

	const handleEditClose = () => {
		setEditingRecord(null);
		setEditingValues({
			checkInTime: '',
			checkOutTime: '',
		});
		setIsSavingEdit(false);
	};

	const handleEditingValueChange = (event) => {
		const { name, value } = event.target;
		setEditingValues((currentValues) => ({
			...currentValues,
			[name]: value,
		}));
	};

	const handleEditSave = async (event) => {
		event.preventDefault();

		if (!editingRecord) {
			return;
		}

		const checkInDate = new Date(editingValues.checkInTime);
		const checkOutDate = editingValues.checkOutTime
			? new Date(editingValues.checkOutTime)
			: null;

		if (Number.isNaN(checkInDate.getTime())) {
			toast.error('Start time is required.');
			return;
		}

		if (checkOutDate && Number.isNaN(checkOutDate.getTime())) {
			toast.error('End time is invalid.');
			return;
		}

		if (checkOutDate && checkOutDate <= checkInDate) {
			toast.error('End time must be after the start time.');
			return;
		}

		setIsSavingEdit(true);

		try {
			await dispatch(
				updateAttendance({
					id: editingRecord.id,
					requestedUserId: editingRecord.userId || selfAccount?.id,
					date: checkInDate.toISOString(),
					checkInTime: checkInDate.toISOString(),
					checkOutTime: checkOutDate ? checkOutDate.toISOString() : null,
					isBreak: editingRecord.isBreak,
					// Keep any recorded location intact when editing times.
					latitudeIn: editingRecord.latitudeIn,
					longitudeIn: editingRecord.longitudeIn,
					latitudeOut: editingRecord.latitudeOut,
					longitudeOut: editingRecord.longitudeOut,
				})
			).unwrap();
			await refreshAttendance();
			toast.success('Time entry updated.');
			handleEditClose();
		} catch (error) {
			setIsSavingEdit(false);
			toast.error(getErrorMessage(error));
		}
	};

	const handleSickPayCreate = async ({ date, hours }) => {
		if (!selfAccount?.id) {
			toast.error('Your account is still loading. Try again in a moment.');
			return;
		}

		const safeHours = Number(hours);
		if (!Number.isFinite(safeHours) || safeHours <= 0) {
			toast.error('Paid hours must be greater than 0.');
			return;
		}

		const targetUserId = isSuperUser ? viewedEmployeeId : selfAccount.id;
		if (!targetUserId) {
			toast.error('No employee selected.');
			return;
		}

		if (viewedOpenWorkRecord || viewedOpenBreakRecord) {
			toast.error('This employee has an open session. Close it before adding sick pay.');
			return;
		}

		const start = new Date(`${date}T08:00:00`);
		if (Number.isNaN(start.getTime())) {
			toast.error('Date is invalid.');
			return;
		}

		const end = new Date(start.getTime() + safeHours * 60 * 60 * 1000);

		setIsSavingSickPay(true);
		try {
			await dispatch(
				createAttendance({
					UserId: targetUserId,
					Date: start.toISOString(),
					CheckInTime: start.toISOString(),
					CheckOutTime: end.toISOString(),
					IsBreak: false,
				})
			).unwrap();
			await refreshAttendance();
			toast.success('Sick pay entry added.');
			setIsSickPayModalOpen(false);
		} catch (error) {
			toast.error(getErrorMessage(error));
		} finally {
			setIsSavingSickPay(false);
		}
	};

	return (
		<div className="flex h-screen flex-col clockin-page">
			<Header />
			<div className="flex flex-1 overflow-hidden">
				<Sidebar />
				<main className="flex-1 overflow-auto p-3 sm:p-4 md:p-8" id="mobile-display">
					{deviceGate.isPhoneBlocked ? (
						<div className="mx-auto flex min-h-[60vh] max-w-7xl items-center justify-center p-4">
							<PhoneClockInBlocked />
						</div>
					) : (
						<div className="clockin-content mx-auto max-w-7xl space-y-6">
							<div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
								<div>
								<h1 className="flex items-center gap-3 text-2xl font-semibold text-black sm:text-3xl">
									<Clock size={26} />
									Time Clock
								</h1>
								<p className="mt-2 text-sm text-gray-600">
									Paid work hours come from clock-in sessions. Paid break / exit
									time is tracked separately and already included in paid hours.
								</p>
							</div>
								<div className="rounded-md border border-gray-200 bg-white px-4 py-3 shadow-sm">
									<p className="text-sm text-gray-500">Week</p>
								<div className="mt-2 flex items-center gap-3">
									<button
										type="button"
										onClick={() => setSelectedWeekStart(getPreviousWeek(selectedWeekStart))}
										className="rounded-md border border-gray-200 p-2 text-gray-700 transition hover:bg-gray-50"
									>
										<ChevronLeft size={16} />
									</button>
									<p className="min-w-[180px] text-center text-sm font-semibold text-gray-900">
										{getWeekLabel(selectedWeekStart)}
									</p>
									<button
										type="button"
										onClick={() => setSelectedWeekStart(getNextWeek(selectedWeekStart))}
										disabled={isCurrentWeekView}
										className="rounded-md border border-gray-200 p-2 text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
									>
										<ChevronRight size={16} />
									</button>
								</div>
								<div className="mt-3 text-center">
									<button
										type="button"
										onClick={() => setSelectedWeekStart(currentWeekStart)}
										className="text-sm font-medium text-secondColor-600 transition hover:text-secondColor-700"
									>
										This week
									</button>
									</div>
								</div>
								{isSuperUser ? (
									<div className="rounded-md border border-gray-200 bg-white px-4 py-3 shadow-sm">
										<p className="text-sm text-gray-500">Employee</p>
										<select
											value={selectedEmployeeId}
											onChange={(event) =>
												setSelectedEmployeeId(
													String(event.target.value || '').toLowerCase()
												)
											}
											className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900"
										>
											{activeEmployees.map((user) => (
												<option
													key={user.id}
													value={String(user.id).toLowerCase()}
												>
													{getUserDisplayName(user)}
												</option>
											))}
										</select>
										<p className="mt-2 text-xs text-gray-500">
											Choose an employee to recalculate the summaries below.
										</p>
									</div>
								) : null}
							</div>

							<SectionCard
								title="Current status"
								subtitle={
									isSuperUser && viewedEmployeeId !== selfAccountId
										? `${statusConfig.hint} You are viewing ${viewedEmployeeLabel} below, but these buttons affect your own time only.`
										: statusConfig.hint
								}
								action={
									<StatusPill label={statusConfig.label} tone={statusConfig.tone} />
								}
						>
							<div className="grid gap-5 lg:grid-cols-[1.4fr,1fr]">
								<div className="rounded-md border border-gray-200 bg-gray-50 p-5">
									<p className="text-sm text-gray-500">Current time</p>
									<div className="mt-2 text-3xl font-semibold text-gray-900 sm:text-4xl">
										<SimpleClock value={currentTime} setValue={setCurrentTime} />
									</div>
									<p className="mt-3 text-sm text-gray-500">
										{openBreakRecord
											? `Break running ${formatDuration(
													getOngoingDurationMinutes(openBreakRecord, currentTime)
											  )}`
											: openWorkRecord
												? `Shift running ${formatDuration(
														getOngoingDurationMinutes(openWorkRecord, currentTime)
												  )}`
												: 'No open session right now.'}
									</p>
								</div>

								<div className="flex flex-wrap gap-3">
									<button
										type="button"
										onClick={() =>
											requestClockAction({
												kind: 'create',
												isBreak: false,
												label: 'Clock In',
												successMessage: 'Clocked in.',
											})
										}
										disabled={Boolean(openWorkRecord || openBreakRecord || isBusy)}
										className="inline-flex w-full items-center gap-2 rounded-md border border-secondColor-600 px-4 py-3 text-sm font-medium text-secondColor-600 transition hover:bg-secondColor-50 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
									>
										<Play size={16} />
										Clock In
									</button>
									<button
										type="button"
										onClick={() =>
											requestClockAction({
												kind: 'create',
												isBreak: true,
												label: 'Start Break',
												successMessage: 'Paid break started.',
											})
										}
										disabled={Boolean(!openWorkRecord || openBreakRecord || isBusy)}
										className="inline-flex w-full items-center gap-2 rounded-md border border-amber-500 px-4 py-3 text-sm font-medium text-amber-700 transition hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
									>
										<Coffee size={16} />
										Start Break
									</button>
									<button
										type="button"
										onClick={() =>
											requestClockAction({
												kind: 'close',
												isBreak: true,
												record: openBreakRecord,
												label: 'End Break',
												successMessage: 'Paid break ended.',
											})
										}
										disabled={Boolean(!openBreakRecord || isBusy)}
										className="inline-flex w-full items-center gap-2 rounded-md border border-amber-500 bg-amber-500 px-4 py-3 text-sm font-medium text-white transition hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
									>
										<Circle size={16} />
										End Break
									</button>
									<button
										type="button"
										onClick={() =>
											requestClockAction({
												kind: 'close',
												isBreak: false,
												record: openWorkRecord,
												label: 'Clock Out',
												successMessage: 'Clocked out.',
											})
										}
										disabled={Boolean(!openWorkRecord || openBreakRecord || isBusy)}
										className="inline-flex w-full items-center gap-2 rounded-md border border-red-500 bg-red-500 px-4 py-3 text-sm font-medium text-white transition hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
									>
										<Circle size={16} />
										Clock Out
									</button>
								</div>
								</div>
							</SectionCard>

								<SectionCard
									title="Viewing"
									subtitle={
										isSuperUser
											? 'Pick any employee to review their hours, overtime, and session history.'
											: 'Your weekly time summary.'
									}
									action={
										canManageSickPay ? (
											<button
												type="button"
												onClick={() => setIsSickPayModalOpen(true)}
												disabled={isBusy || isSavingSickPay}
												className="inline-flex items-center rounded-md border border-secondColor-600 px-4 py-2 text-sm font-medium text-secondColor-600 transition hover:bg-secondColor-50 disabled:cursor-not-allowed disabled:opacity-50"
											>
												Add Sick Pay Day
											</button>
										) : null
									}
								>
									<div className="flex flex-wrap items-center gap-4">
										<div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3">
											<p className="text-sm text-gray-500">Employee</p>
											<p className="mt-1 text-lg font-semibold text-gray-900">
											{viewedEmployeeLabel}
										</p>
									</div>
									{isSuperUser ? (
										<div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3">
											<p className="text-sm text-gray-500">Team members</p>
											<p className="mt-1 text-lg font-semibold text-gray-900">
												{activeEmployees.length}
											</p>
										</div>
									) : null}
								</div>
							</SectionCard>

							<div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
							<SummaryCard
								title="Today Paid Hours"
								value={formatDuration(todaySummary.workMinutes)}
								hint="From worked-time sessions"
								icon={Briefcase}
								tone="text-green-600"
							/>
							<SummaryCard
								title="Today Break / Exit"
								value={formatDuration(todaySummary.breakMinutes)}
								hint={`${todaySummary.breakCount} break session${
									todaySummary.breakCount === 1 ? '' : 's'
								}`}
								icon={Coffee}
								tone="text-amber-600"
							/>
							<SummaryCard
								title="Week Paid Hours"
								value={formatDuration(weekTotals.paidMinutes)}
								hint="Break / exit time is already included here"
								icon={Clock}
								tone="text-secondColor-600"
							/>
							<SummaryCard
								title="Regular Hours"
								value={formatDuration(weekTotals.regularMinutes)}
								hint="Up to 40 hours"
								icon={Calendar}
								tone="text-slate-600"
							/>
							<SummaryCard
								title="Overtime"
								value={formatDuration(weekTotals.overtimeMinutes)}
								hint="Anything over 40 hours"
								icon={Briefcase}
								tone="text-red-600"
							/>
							<SummaryCard
								title="Break Frequency"
								value={String(weekTotals.breakCount)}
								hint={`${formatDuration(weekTotals.breakMinutes)} this week`}
								icon={Coffee}
								tone="text-amber-600"
							/>
						</div>

							<SectionCard
								title="This Week Daily Totals"
								subtitle={`Monday through Sunday for ${viewedEmployeeLabel}`}
							>
								<DailySummaryTable summaries={weekSummaries} />
							</SectionCard>

							<SectionCard
								title={isSuperUser ? 'Selected Employee Session History' : 'My Session History'}
								subtitle={`Raw saved records for ${viewedEmployeeLabel} in the selected week`}
							>
								<SessionTable
									records={personalWeekRecords}
								currentTime={currentTime}
								canEdit={canEditAttendance}
								onEdit={handleEditOpen}
								onViewLocation={setLocationRecord}
								emptyLabel="No time records in this week yet."
							/>
						</SectionCard>

							{isSuperUser ? (
								<SectionCard
									title="Team Weekly Totals"
									subtitle="Every active employee in the selected week"
								>
									<TeamTotalsTable
										rows={teamTotalsRows}
										selectedEmployeeId={viewedEmployeeId}
										onSelectEmployee={(userId) =>
											setSelectedEmployeeId(String(userId).toLowerCase())
										}
									/>
								</SectionCard>
							) : null}

							<SectionCard
								title="Payroll Snapshot"
								subtitle={`Weekly totals for ${viewedEmployeeLabel}`}
							>
							<div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
								<div className="rounded-md border border-gray-200 bg-gray-50 p-4">
									<p className="text-sm text-gray-500">Paid Hours</p>
									<p className="mt-2 text-2xl font-semibold text-gray-900">
										{formatDuration(weekTotals.paidMinutes)}
									</p>
								</div>
								<div className="rounded-md border border-gray-200 bg-gray-50 p-4">
									<p className="text-sm text-gray-500">Remaining to 40</p>
									<p className="mt-2 text-2xl font-semibold text-gray-900">
										{formatDuration(weekTotals.remainingMinutes)}
									</p>
								</div>
								<div className="rounded-md border border-gray-200 bg-gray-50 p-4">
									<p className="text-sm text-gray-500">Paid Break / Exit</p>
									<p className="mt-2 text-2xl font-semibold text-gray-900">
										{formatDuration(weekTotals.breakMinutes)}
									</p>
								</div>
								<div className="rounded-md border border-gray-200 bg-gray-50 p-4">
									<p className="text-sm text-gray-500">Edit Access</p>
									<p className="mt-2 text-lg font-semibold text-gray-900">
										{canEditAttendance ? 'Enabled for this account' : 'View only'}
									</p>
								</div>
							</div>
						</SectionCard>
					</div>
					)}
				</main>
			</div>

			<EditAttendanceModal
				record={editingRecord}
				values={editingValues}
				onChange={handleEditingValueChange}
				onClose={handleEditClose}
				onSubmit={handleEditSave}
				isSaving={isSavingEdit}
			/>

			<ClockCaptureModal
				isOpen={Boolean(pendingClockAction)}
				actionLabel={pendingClockAction?.label || ''}
				onCapture={handleClockCapture}
				onClose={() => {
					if (!isClockSaving) {
						setPendingClockAction(null);
					}
				}}
				isSaving={isClockSaving}
			/>

			<LocationModal
				record={locationRecord}
				onClose={() => setLocationRecord(null)}
			/>

			<SickPayModal
				isOpen={isSickPayModalOpen}
				onClose={() => {
					if (!isSavingSickPay) {
						setIsSickPayModalOpen(false);
					}
				}}
				onSubmit={handleSickPayCreate}
				isSaving={isSavingSickPay}
				employeeLabel={viewedEmployeeLabel}
			/>
		</div>
	);
}
